import { useCallback, useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { Switch } from "@component/switch";
import { Input } from "@component/input";
import { AlertCircleIcon, CheckIcon, CopyIcon, Loader2Icon, MessageCircleIcon } from "lucide-react";
import { getJobWhatsappAdLink, WhatsappAdLink } from "@service/job";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";

interface Props {
    isOpen: boolean;
    jobId: string;
    onClose: () => void;
}

/**
 * "Get WhatsApp ad link" dialog (#3088, BE ourteam-backend#3664). Fetches the
 * per-job WhatsApp deep link (REF-coded) and lets HR copy it into a paid IG/FB
 * ad. The link is copied VERBATIM from the BE — never reconstructed client-side,
 * so the embedded REF code can't drift.
 */
export default function WhatsappAdLinkDialog({ isOpen, jobId, onClose }: Props) {
    const snackbar = useSnackbar();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [adLink, setAdLink] = useState<WhatsappAdLink | null>(null);
    const [error, setError] = useState<boolean>(false);
    const [copied, setCopied] = useState<boolean>(false);
    // Guard against a fast job switch (dialog reopened for job B while job A is
    // in flight) landing A's REF-coded link on B's view — that would let HR copy
    // the wrong job's ad link.
    const requestRef = useRef(0);

    const fetchLink = useCallback(() => {
        if (!jobId) return;
        const requestId = ++requestRef.current;
        setIsLoading(true);
        setError(false);
        setAdLink(null);
        setCopied(false);
        setUseShortLink(false); // default back to the raw link on each open
        getJobWhatsappAdLink(jobId)
            .then((data) => {
                if (requestId !== requestRef.current) return;
                if (data?.link) {
                    setAdLink(data);
                } else {
                    setError(true);
                }
            })
            .catch((err) => {
                if (requestId !== requestRef.current) return;
                catchErrorLog(`Failed to fetch WhatsApp ad link: ${err}`);
                setError(true);
            })
            .finally(() => {
                if (requestId === requestRef.current) setIsLoading(false);
            });
    }, [jobId]);

    useEffect(() => {
        if (isOpen && jobId) fetchLink();
        return () => { requestRef.current += 1; };
    }, [isOpen, jobId, fetchLink]);

    // #3269: HR can flip between the raw wa.me link (default) and the stable
    // short marketing link, when the BE provides one.
    const [useShortLink, setUseShortLink] = useState<boolean>(false);
    const displayLink = adLink ? (useShortLink && adLink.short_link ? adLink.short_link : adLink.link) : "";

    const handleCopy = async () => {
        if (!displayLink) return;
        try {
            await navigator.clipboard.writeText(displayLink);
            setCopied(true);
            snackbar.showSuccess("WhatsApp ad link copied to clipboard.");
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            catchErrorLog(`Clipboard write failed: ${err}`);
            snackbar.showError("Couldn't copy the link. Please copy it manually.");
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="sm:max-w-lg border-0 outline-none ring-0 focus:outline-none focus-visible:outline-none focus-visible:ring-0">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MessageCircleIcon className="w-5 h-5 text-green-600" />
                        WhatsApp ad link
                    </DialogTitle>
                    <DialogDescription>
                        Paste this link into your Instagram / Facebook ad. Candidates who tap it start this job's hiring flow on WhatsApp.
                    </DialogDescription>
                </DialogHeader>

                {isLoading ? (
                    <div className="py-10 flex items-center justify-center">
                        <Loader2Icon className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                ) : error ? (
                    <div className="py-6 flex flex-col items-center text-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                            <AlertCircleIcon className="w-6 h-6" />
                        </div>
                        <p className="text-sm text-gray-600">
                            We couldn't generate the WhatsApp ad link right now. Please try again in a moment.
                        </p>
                        <Button variant="outline" onClick={fetchLink}>
                            Try again
                        </Button>
                    </div>
                ) : adLink ? (
                    <div className="space-y-4">
                        {/* The link — read-only, copied verbatim */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">Ad link</label>
                            <div className="flex items-center gap-2">
                                <Input
                                    readOnly
                                    value={displayLink}
                                    onFocus={(e) => e.currentTarget.select()}
                                    className="font-mono text-xs"
                                />
                                <Button onClick={handleCopy} className="flex-shrink-0">
                                    {copied ? (
                                        <><CheckIcon className="w-4 h-4" /> Copied</>
                                    ) : (
                                        <><CopyIcon className="w-4 h-4" /> Copy</>
                                    )}
                                </Button>
                            </div>
                            {/* Short-link toggle (#3269) — only when the BE minted one. */}
                            {adLink.short_link && (
                                <div className="flex items-center justify-between pt-1">
                                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Use short link</p>
                                    <Switch
                                        checked={useShortLink}
                                        onCheckedChange={(checked) => { setUseShortLink(checked); setCopied(false); }}
                                        className="flex-shrink-0"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Pre-filled message preview */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">Candidate's pre-filled message</label>
                            <div className="rounded-lg bg-green-50 border border-green-100 px-3 py-2.5 text-sm text-gray-700">
                                {adLink.prefilled_text}
                            </div>
                        </div>

                        <p className="text-xs text-gray-500">
                            The link already carries this job's tracking code — copy it exactly as shown, don't edit the text.
                        </p>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
